import {
    generateAccountWithSnowball,
    createTransactionsAndExceptions,
    calculateAggregateStatistics
} from '../utilities/schedule-utilities';
import { checkAllAccountsForSchedule } from '../utilities/account-utilities';

/**
 * Service for managing snowball payment calculations
 * Separates calculation logic from components and provides a clean API
 */
export class SnowballCalculationService {
    /**
     * Generates payment schedule for a single account
     */
    static generatePaymentScheduleForAccount(account, accountIndex, allAccounts, snowballStarterLump = 0) {
        return generateAccountWithSnowball(account, accountIndex, allAccounts, snowballStarterLump);
    }

    /**
     * Calculates payment schedules for all accounts with snowball effect
     * Performs multiple passes to ensure snowball calculations are correct
     */
    static calculateAllAccountSchedules(accounts, snowballStarterLump = 0, applySort = false) {
        if (!accounts || accounts.length === 0) {
            return [];
        }

        let updatedAccounts = [...accounts];
        const MAX_ITERATIONS = 10;
        
        // Perform multiple calculation passes to ensure snowball effect is properly applied
        for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
            // Generate payment schedules for all accounts
            updatedAccounts = updatedAccounts.map((account, index) =>
                this.generatePaymentScheduleForAccount(account, index, updatedAccounts, snowballStarterLump)
            );
            
            // Sort by payoff time if requested (typically done once)
            if (applySort && iteration === 0) {
                updatedAccounts.sort((a, b) => {
                    const aLength = a.paymentSchedule ? a.paymentSchedule.length : Infinity;
                    const bLength = b.paymentSchedule ? b.paymentSchedule.length : Infinity;
                    return aLength - bLength;
                });
                
                // Update indices after sorting
                updatedAccounts.forEach((account, index) => {
                    account.index = index;
                });
            }
            
            // Recalculate with updated snowball data
            updatedAccounts = updatedAccounts.map((account, index) =>
                this.generatePaymentScheduleForAccount(account, index, updatedAccounts, snowballStarterLump)
            );
        }

        // Final sort if requested to ensure accounts are in correct order after all calculations
        if (applySort) {
            updatedAccounts.sort((a, b) => {
                const aLength = a.paymentSchedule ? a.paymentSchedule.length : Infinity;
                const bLength = b.paymentSchedule ? b.paymentSchedule.length : Infinity;
                return aLength - bLength;
            });
            
            // Update final indices
            updatedAccounts.forEach((account, index) => {
                account.index = index;
            });
        }

        return updatedAccounts;
    }

    /**
     * Generates transaction data for all accounts
     */
    static generateTransactionData(accounts, debitAccountID) {
        if (!accounts || accounts.length === 0 || !debitAccountID) {
            return [];
        }

        return accounts
            .filter(account => account.paymentSchedule && account.paymentSchedule.length > 0)
            .map(account => createTransactionsAndExceptions(account, debitAccountID));
    }

    /**
     * Calculates aggregate results for all accounts
     */
    static calculateAggregateResults(accounts) {
        return calculateAggregateStatistics(accounts);
    }

    /**
     * Validates that all accounts have valid payment schedules
     */
    static validateAccountSchedules(accounts) {
        return checkAllAccountsForSchedule(accounts);
    }

    /**
     * Checks if accounts are ready for calculation
     */
    static canCalculateSchedules(accounts, debitAccountID) {
        return (
            accounts && 
            accounts.length > 0 && 
            debitAccountID && 
            accounts.every(account => 
                account.balance > 0 && 
                account.paymentType &&
                (account.paymentType !== 'fixed' || account.fixed_payment > 0)
            )
        );
    }

    /**
     * Creates a calculation context with all necessary data
     */
    static createCalculationContext(accounts, snowballStarterLump, debitAccountID, applySort = false) {
        const calculatedAccounts = this.calculateAllAccountSchedules(accounts, snowballStarterLump, applySort);
        const transactions = this.generateTransactionData(calculatedAccounts, debitAccountID);
        const aggregateResults = this.calculateAggregateResults(calculatedAccounts);
        const isValid = this.validateAccountSchedules(calculatedAccounts);

        return {
            accounts: calculatedAccounts,
            transactions,
            aggregateResults,
            isValid,
            canCreateTransactions: isValid && transactions.length > 0
        };
    }
}